import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChatTeaserBubble } from '@/components/chatbot/ChatTeaserBubble';
import { ChatHeader } from '@/components/chatbot/ChatHeader';
import { ChatMessages } from '@/components/chatbot/ChatMessages';
import { ChatQuickReplies } from '@/components/chatbot/ChatQuickReplies';
import { ChatInput } from '@/components/chatbot/ChatInput';
import { AVATAR_URL, CAL_LINK, BOOK_REPLY } from '@/components/chatbot/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_OPTIONS = [
  'Commercial / Brand Film',
  'Resort & Tourism Film',
  'Real Human Stories',
  'Event Coverage',
  'Something else',
];

const BUDGET_OPTIONS = ['Under $5k', '$5k–$15k', '$15k–$50k', '$50k+', 'Not sure yet'];

const PLACEHOLDERS = {
  name: 'Your name...',
  email: 'you@email.com',
  phone: 'Best contact number...',
  project: 'Tell me about your project...',
  budget: 'Your approximate budget...',
  book: 'Ask me anything...',
  chat: 'Ask me anything...',
};

const GREETING =
  "G'day! I'm Terry's assistant. I'll grab a couple of quick details so Terry can personally follow up with you — then we can lock in a call.\n\nFirst up, what's your name?";

const isValidEmail = (text) => /^[\w.+-]+@[\w-]+\.[\w.-]{2,}$/.test(text.trim());
const isValidPhone = (text) => (text.match(/\d/g) || []).length >= 8;
const firstName = (name) => (name || '').trim().split(/\s+/)[0];

// Detects when a user politely declines to share something (email, phone, etc.)
const isDeclining = (text) => {
  const t = (text || '').toLowerCase().trim();
  if (t.length < 2) return false;
  const declineWords = [
    "don't want", 'dont want', 'do not want',
    'not yet', 'not now', 'later', 'skip', 'pass',
    "prefer not", 'rather not', "won't share", 'wont share',
    'no thanks', 'no thank you',
    'not comfortable', 'not right now', 'nope',
    "won't give", 'wont give', "don't give", 'dont give',
  ];
  return declineWords.some((phrase) => t.includes(phrase));
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleDismissed, setBubbleDismissed] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [step, setStep] = useState('name');
  const [quickReplies, setQuickReplies] = useState([]);
  const leadRef = useRef({ name: '', email: '', phone: '', inquiry_type: '', budget: '' });

  useEffect(() => {
    if (!isOpen && !bubbleDismissed) {
      const timer = setTimeout(() => setShowBubble(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, bubbleDismissed]);

  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
      setBubbleDismissed(true);
    }
  }, [isOpen]);

  const saveProgress = (fields, completed = false) => {
    axios
      .post(`${BACKEND_URL}/api/chatbot-lead/progress`, { session_id: sessionId, ...fields, completed })
      .catch(() => {
        /* lead persistence is retried on the next step */
      });
  };

  const botReply = (content, replies = []) => {
    setIsLoading(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { role: 'assistant', content }]);
      setQuickReplies(replies);
      setIsLoading(false);
    }, 600);
  };

  const sendToAI = async (messageText) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: messageText,
        session_id: sessionId,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `No worries — Terry has your details and will be in touch soon. You can also book a call directly: ${CAL_LINK}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserMessage = (rawText) => {
    const text = rawText.trim();
    if (!text || isLoading) return;

    setInput('');
    setQuickReplies([]);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);

    switch (step) {
      case 'name': {
        leadRef.current.name = text;
        saveProgress({ name: text });
        setStep('email');
        botReply(`Nice to meet you, ${firstName(text)}! What's the best email address for Terry to reach you on?`);
        break;
      }
      case 'email': {
        if (isDeclining(text)) {
          leadRef.current.email = '';
          setStep('phone');
          botReply(
            `No worries, ${firstName(leadRef.current.name)} — happy to keep it light. What's the best phone number to reach you on? (Feel free to skip that too if you'd rather.)`
          );
          return;
        }
        if (!isValidEmail(text)) {
          botReply("Hmm, that doesn't quite look like an email address — mind double-checking it for me?");
          return;
        }
        leadRef.current.email = text;
        saveProgress({ name: leadRef.current.name, email: text });
        setStep('phone');
        botReply("Perfect. And the best phone number? Just in case we get cut off — Terry can call you back directly.");
        break;
      }
      case 'phone': {
        if (isDeclining(text)) {
          leadRef.current.phone = '';
          saveProgress({ ...leadRef.current });
          setStep('project');
          botReply("All good — we'll skip that too. What type of project are you looking at?", PROJECT_OPTIONS);
          return;
        }
        if (!isValidPhone(text)) {
          botReply("That number doesn't look complete — could you pop in your full phone number? (Or say 'skip' if you'd rather not.)");
          return;
        }
        leadRef.current.phone = text;
        saveProgress({ ...leadRef.current });
        setStep('project');
        botReply("Got it — your details are safely with Terry. Now, what type of project are you looking at?", PROJECT_OPTIONS);
        break;
      }
      case 'project': {
        leadRef.current.inquiry_type = text;
        saveProgress({ ...leadRef.current });
        setStep('budget');
        botReply("Great choice. And what sort of investment are you working with for this project?", BUDGET_OPTIONS);
        break;
      }
      case 'budget': {
        leadRef.current.budget = text;
        saveProgress({ ...leadRef.current }, true);
        setStep('book');
        const hasContact = Boolean(leadRef.current.email) || Boolean(leadRef.current.phone);
        const nameGreeting = firstName(leadRef.current.name)
          ? `Brilliant, ${firstName(leadRef.current.name)}! `
          : 'Brilliant! ';
        const bookMessage = hasContact
          ? `${nameGreeting}Terry has all your details and will be in touch shortly.\n\nWant to skip the queue? Lock in a free 30-minute discovery call with Terry now:`
          : `${nameGreeting}The easiest way to chat with Terry from here is to lock in a free 30-minute discovery call — you can pick a time that suits you and Terry will call:`;
        botReply(bookMessage, [BOOK_REPLY]);
        break;
      }
      default: {
        sendToAI(text);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleUserMessage(input);
  };

  const handleQuickReply = (reply) => {
    if (reply === BOOK_REPLY) {
      window.open(CAL_LINK, '_blank');
      setQuickReplies([]);
      setStep('chat');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Legend! Pick a time that suits you and Terry will call you then.\n\nGot any questions about Terry's work in the meantime? Fire away.",
        },
      ]);
    } else {
      handleUserMessage(reply);
    }
  };

  return (
    <>
      <ChatTeaserBubble visible={showBubble && !isOpen} onClick={() => setIsOpen(true)} />

      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform overflow-hidden border-2 border-[#007AFF]"
            data-testid="chatbot-toggle"
            aria-label="Open chat"
          >
            <img src={AVATAR_URL} alt="Chat with Terry's Assistant" className="w-full h-full object-cover" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed z-50 bg-[#0C0C0C] border border-[#27272A] shadow-2xl flex flex-col
              md:bottom-6 md:right-6 md:w-[380px] md:max-w-[calc(100vw-48px)] md:rounded-lg md:h-[550px]
              inset-0 md:inset-auto h-[100dvh]"
            data-testid="chatbot-window"
          >
            <ChatHeader onClose={() => setIsOpen(false)} />
            <ChatMessages messages={messages} isLoading={isLoading} />
            {!isLoading && <ChatQuickReplies replies={quickReplies} onSelect={handleQuickReply} />}
            <ChatInput
              value={input}
              onChange={setInput}
              onSubmit={handleSubmit}
              placeholder={PLACEHOLDERS[step] || 'Type a message...'}
              disabled={isLoading}
              autoFocus={isOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
